import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileDown, Pencil, Receipt, Undo2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { apiErrorCode, apiErrorMessage } from '@/api/client'
import { cashReceipts as api } from '@/api/endpoints'
import { Alert, EmptyState, Field, Input, Modal, Select, Spinner } from '@/components/ui'
import Pagination from '@/components/ui/Pagination'
import { useBootstrap } from '@/hooks/useBootstrap'
import { formatDate, formatDateTime, formatMoney } from '@/lib/utils'
import { useAuth } from '@/store/auth'
import { PaymentType, type CashReceipt } from '@/types/api'
import { CenterFilter, useCenterFilter, useCenters } from '@/features/centers/useCenters'

export default function CashReceiptsPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const readOnly = !useAuth((s) => s.user)?.can_write
  const { data: boot } = useBootstrap()

  const [params, setParams] = useSearchParams()
  const issueFor = params.get('client_reservation_id')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [correcting, setCorrecting] = useState<CashReceipt | null>(null)

  const enabled = boot?.settings.module_cash_receipt ?? false

  // Strediská (`sql/040`) - spoločný filter; stĺpec len pri viacerých.
  const { multi: multiCenter, nameOf } = useCenters()
  const [centerIds] = useCenterFilter()
  const { data, isLoading } = useQuery({
    queryKey: ['cash-receipts', page, pageSize, centerIds.join(',')],
    queryFn: () =>
      api.list({ page, limit: pageSize, centers: centerIds.length ? centerIds.join(',') : undefined }),
    enabled,
    placeholderData: keepPreviousData,
  })

  const storno = useMutation({
    mutationFn: (id: number) => api.storno(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cash-receipts'] }),
  })

  if (!enabled) {
    return <Alert variant="warning">{t('cashReceipts.moduleDisabled')}</Alert>
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold">{t('cashReceipts.title')}</h1>
        <span className="text-sm text-slate-400">{data?.count ?? 0}</span>
        <CenterFilter className="ml-auto w-56" />
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-slate-400">
            <Spinner className="h-5 w-5" />
          </div>
        ) : (data?.results.length ?? 0) === 0 ? (
          <EmptyState>{t('app.empty')}</EmptyState>
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th>{t('cashReceipts.number')}</th>
                <th>{t('changelog.created')}</th>
                <th>{t('cashReceipts.issuedTo')}</th>
                {multiCenter ? <th>{t('centers.center')}</th> : null}
                <th>{t('cashReceipts.paymentType')}</th>
                <th className="text-right">{t('cashReceipts.amount')}</th>
                <th>{t('global.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {data?.results.map((receipt) => (
                <tr key={receipt.id} className={receipt.storno ? 'opacity-50' : ''}>
                  <td className="font-medium">
                    {receipt.document_number}
                    {receipt.storno ? (
                      <span className="ml-1.5 rounded bg-red-100 px-1 text-xs text-red-700">
                        {t('cashReceipts.storno')}
                      </span>
                    ) : null}
                  </td>
                  <td>{formatDate(receipt.created)}</td>
                  <td>{receipt.receipt_name || '—'}</td>
                  {multiCenter ? <td className="text-slate-600">{nameOf(receipt.center_id) || '—'}</td> : null}
                  <td className="text-slate-600">
                    {t(`reservation.payment.${receipt.payment_type ?? 1}`)}
                  </td>
                  <td className="text-right tabular-nums">{formatMoney(receipt.amount)}</td>
                  <td>
                    <div className="flex gap-0.5">
                      <a
                        href={api.pdfUrl(receipt.id, receipt.storno)}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-ghost p-1"
                        title={t('global.download')}
                      >
                        <FileDown size={14} />
                      </a>
                      {!readOnly && !receipt.storno ? (
                        <button
                          type="button"
                          className="btn-ghost p-1"
                          title={t('cashReceipts.correct')}
                          onClick={() => setCorrecting(receipt)}
                        >
                          <Pencil size={14} />
                        </button>
                      ) : null}
                      {!readOnly && !receipt.storno ? (
                        <button
                          type="button"
                          className="btn-ghost p-1 text-red-500"
                          title={t('cashReceipts.storno')}
                          onClick={() => {
                            if (window.confirm(t('cashReceipts.stornoConfirm')))
                              storno.mutate(receipt.id)
                          }}
                        >
                          <Undo2 size={14} />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <Pagination
          page={page}
          pages={Math.max(1, Math.ceil((data?.count ?? 0) / pageSize))}
          pageSize={pageSize}
          count={data?.count ?? 0}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
        />
      </div>

      {correcting ? (
        <CorrectModal
          receipt={correcting}
          onClose={() => setCorrecting(null)}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['cash-receipts'] })}
        />
      ) : null}

      {issueFor ? (
        <IssueModal
          reservationId={Number.parseInt(issueFor, 10)}
          onClose={() => setParams({})}
          onIssued={() => void qc.invalidateQueries({ queryKey: ['cash-receipts'] })}
        />
      ) : null}
    </div>
  )
}

function IssueModal({
  reservationId,
  onClose,
  onIssued,
}: {
  reservationId: number
  onClose: () => void
  onIssued: () => void
}) {
  const { t } = useTranslation()
  const [description, setDescription] = useState('')
  // A cash receipt is proof of a cash payment - that is the sensible default.
  const [paymentType, setPaymentType] = useState<number>(PaymentType.Cash)
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['cash-receipt-preview', reservationId],
    queryFn: () => api.preview(reservationId),
  })

  const issue = useMutation({
    mutationFn: () => api.issue(reservationId, description, paymentType),
    onSuccess: (receipt) => {
      onIssued()
      window.open(api.pdfUrl(receipt.id), '_blank')
      onClose()
    },
    onError: (err) => {
      setError(
        apiErrorCode(err) === 'cashreceipt.nothingToInvoice'
          ? t('cashReceipts.nothingToInvoice')
          : apiErrorMessage(err),
      )
    },
  })

  useEffect(() => setError(null), [reservationId])

  return (
    <Modal
      open
      onClose={onClose}
      title={t('cashReceipts.issue')}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('global.cancel')}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={issue.isPending || !data || data.unpaid_fees.length === 0}
            onClick={() => issue.mutate()}
          >
            {issue.isPending ? <Spinner /> : <Receipt size={15} />}
            {t('cashReceipts.issue')}
          </button>
        </>
      }
    >
      {isLoading || !data ? (
        <div className="flex h-24 items-center justify-center text-slate-400">
          <Spinner className="h-5 w-5" />
        </div>
      ) : (
        <div className="space-y-2">
          {error ? <Alert variant="error">{error}</Alert> : null}

          <div className="text-sm">
            <div className="font-medium">{data.client.full_name}</div>
            <div className="text-slate-500">
              {formatDate(data.week_start)} – {formatDate(data.week_end)} · {data.next_document_number}
            </div>
          </div>

          <div>
            <div className="mb-1 text-xs font-medium uppercase text-slate-500">
              {t('cashReceipts.unpaidFees')}
            </div>
            {data.unpaid_fees.length === 0 ? (
              <Alert variant="warning">{t('cashReceipts.nothingToInvoice')}</Alert>
            ) : (
              <ul className="space-y-1 text-sm">
                {data.unpaid_fees.map((fee) => (
                  <li key={fee.id} className="flex justify-between gap-3">
                    <span className="truncate text-slate-600">
                      {formatDateTime(fee.reservation_start)} ·{' '}
                      {fee.description || fee.type_name || '—'}
                    </span>
                    <span className="tabular-nums">{formatMoney(fee.amount)}</span>
                  </li>
                ))}
                <li className="flex justify-between border-t border-slate-200 pt-1 font-semibold">
                  <span>{t('global.total')}</span>
                  <span className="tabular-nums">{formatMoney(data.amount)}</span>
                </li>
              </ul>
            )}
          </div>

          <Field label={t('cashReceipts.paymentType')}>
            <Select
              value={paymentType}
              onChange={(e) => setPaymentType(Number.parseInt(e.target.value, 10))}
            >
              {[
                PaymentType.Unpaid,
                PaymentType.Cash,
                PaymentType.Card,
                PaymentType.Transfer,
                PaymentType.Invoice,
                PaymentType.Other,
              ].map((p) => (
                <option key={p} value={p}>
                  {t(`reservation.payment.${p}`)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('global.note')}>
            <Input
              maxLength={100}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
        </div>
      )}
    </Modal>
  )
}


function CorrectModal({
  receipt,
  onClose,
  onSaved,
}: {
  receipt: CashReceipt
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    receipt_name: receipt.receipt_name ?? '',
    receipt_description: receipt.receipt_description ?? '',
    payment_type: receipt.payment_type as number,
  })
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => api.correct(receipt.id, form),
    onSuccess: (updated) => {
      onSaved()
      // The PDF was re-rendered server side - show the corrected document.
      window.open(api.pdfUrl(updated.id), '_blank')
      onClose()
    },
    onError: (err) =>
      setError(
        apiErrorCode(err) === 'cashreceipt.voided'
          ? t('cashReceipts.voided')
          : apiErrorMessage(err),
      ),
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={`${t('cashReceipts.correct')} — ${receipt.document_number}`}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('global.cancel')}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={save.isPending}
            onClick={() => {
              setError(null)
              save.mutate()
            }}
          >
            {save.isPending ? <Spinner /> : null}
            {t('global.save')}
          </button>
        </>
      }
    >
      <div className="space-y-2">
        {error ? <Alert variant="error">{error}</Alert> : null}
        <Alert variant="info">{t('cashReceipts.correctHint')}</Alert>

        <Field label={t('cashReceipts.issuedTo')}>
          <Input
            maxLength={100}
            value={form.receipt_name}
            onChange={(e) => setForm({ ...form, receipt_name: e.target.value })}
          />
        </Field>

        <Field label={t('cashReceipts.paymentType')}>
          <Select
            value={form.payment_type}
            onChange={(e) =>
              setForm({ ...form, payment_type: Number.parseInt(e.target.value, 10) })
            }
          >
            {[
              PaymentType.Unpaid,
              PaymentType.Cash,
              PaymentType.Card,
              PaymentType.Transfer,
              PaymentType.Invoice,
              PaymentType.Other,
            ].map((p) => (
              <option key={p} value={p}>
                {t(`reservation.payment.${p}`)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('global.note')}>
          <Input
            maxLength={100}
            value={form.receipt_description}
            onChange={(e) => setForm({ ...form, receipt_description: e.target.value })}
          />
        </Field>

        <div className="rounded-md bg-slate-50 px-3 py-2 text-sm">
          <span className="text-slate-500">{t('cashReceipts.amount')}: </span>
          <strong>{formatMoney(receipt.amount)}</strong>
        </div>
      </div>
    </Modal>
  )
}
